// Story 7.7 — mutation hook for POST /api/v1/admin/feed/editorial/:id/hide
// (admin soft-delete of an editorial card). Invalidates the recent list
// AND the SPA feed cache on success.

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import { ADMIN_EDITORIAL_QUERY_KEY } from "./useCreateEditorialPost";

export interface HideEditorialVariables {
  id: string;
  reason?: string;
}

export interface HideEditorialResponse {
  data: {
    id: string;
    status: string;
    moderation_state: string;
  };
}

export interface HideEditorialError {
  code: string;
  message: string;
  status: number;
}

function freshIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function postHide(
  variables: HideEditorialVariables,
): Promise<HideEditorialResponse> {
  const response = await fetch(
    `/api/v1/admin/feed/editorial/${variables.id}/hide`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-idempotency-key": freshIdempotencyKey(),
      },
      body: JSON.stringify(
        variables.reason ? { reason: variables.reason } : {},
      ),
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: HideEditorialError = {
      code: "EDITORIAL_HIDE_FAILED",
      message: `Hide failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON — keep default.
    }
    throw detail;
  }
  return (await response.json()) as HideEditorialResponse;
}

export function useHideEditorial(): UseMutationResult<
  HideEditorialResponse,
  HideEditorialError,
  HideEditorialVariables
> {
  const queryClient = useQueryClient();
  return useMutation<HideEditorialResponse, HideEditorialError, HideEditorialVariables>({
    mutationFn: postHide,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ADMIN_EDITORIAL_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}
